import { fetchClient } from "@/api/api";
import type {
	getUserProfileInput,
	getUserProfileOutput,
	updateUserInfoInput,
	updateUserInfoOutput,
} from "@/api/custom_types";
import { useApiError, useTranslation } from "@/hooks";
import { normalizeEmptyStringsToNull } from "@/utils/normalize";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { toast } from "@st-marketplace/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PROFILE_SERVICE_KEYS } from "./keys";

const useGetProfile: CustomQueryHook<
	getUserProfileOutput,
	getUserProfileInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PROFILE_SERVICE_KEYS.useGetUserProfile(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/users/{user_id}",
				{ params: { path: payload.path }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useUpdateProfile: CustomMutationHook<
	updateUserInfoInput,
	updateUserInfoOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: PROFILE_SERVICE_KEYS.useUpdateProfile(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/dashboard/users/{user_id}",
				{
					params: { path: payload.path },
					body: normalizeEmptyStringsToNull(payload.body),
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("profile.updateSuccessMessage"));
			queryClient.invalidateQueries({
				queryKey: PROFILE_SERVICE_KEYS.useGetUserProfile(),
			});
		},
	});
};

export { useGetProfile, useUpdateProfile };
